import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTrash, FaEye, FaTimes, FaSearch, FaFileExcel, FaDownload } from "react-icons/fa";
import * as XLSX from "xlsx";
import API from "../api";

const statusColors = { new: "#0ea5e9", read: "#f59e0b", replied: "#10b981", closed: "#6b7280" };
const statuses = ["new", "read", "replied", "closed"];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchContacts = async () => {
    setLoading(true);
    const { data } = await API.get(`/contacts${filter ? `?status=${filter}` : ""}`);
    setContacts(data.contacts);
    setLoading(false);
  };

  useEffect(() => { fetchContacts(); }, [filter]);

  const updateStatus = async (id, status) => {
    await API.patch(`/contacts/${id}`, { status });
    setContacts((prev) => prev.map((c) => c._id === id ? { ...c, status } : c));
    if (selected?._id === id) setSelected((prev) => ({ ...prev, status }));
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await API.delete(`/contacts/${id}`);
    setContacts((prev) => prev.filter((c) => c._id !== id));
    if (selected?._id === id) setSelected(null);
  };

  const filtered = contacts.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    c.service.toLowerCase().includes(search.toLowerCase())
  );

  const downloadExcel = (data, filename) => {
    const rows = data.map((c, i) => ({
      "S.No": i + 1,
      "Name": c.name,
      "Email": c.email,
      "Phone": c.phone || "-",
      "Company": c.company || "-",
      "Service": c.service,
      "Budget": c.budget || "-",
      "Message": c.message,
      "Status": c.status,
      "Date": new Date(c.createdAt).toLocaleString("en-IN"),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      { wch: 6 }, { wch: 20 }, { wch: 28 }, { wch: 16 },
      { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 40 },
      { wch: 10 }, { wch: 22 },
    ];

    // Header style
    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
      const cell = XLSX.utils.encode_cell({ r: 0, c: C });
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "0EA5E9" } },
          alignment: { horizontal: "center" },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-white">Contacts</h1>
          <p className="text-gray-400 text-sm">{contacts.length} Total Enquiries</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter buttons */}
          {["", ...statuses].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                ${filter === s ? "gradient-bg text-white" : "glass text-gray-400 hover:text-white"}`}>
              {s || "All"}
            </button>
          ))}

          {/* Download buttons */}
          <div className="flex gap-2 ml-1">
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={() => downloadExcel(filtered, `enquiries_${filter || "all"}_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-all"
              style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
              title="Download current view as Excel">
              <FaFileExcel />
              <span className="hidden sm:inline">Export</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={async () => {
                const { data } = await API.get("/contacts?limit=10000");
                downloadExcel(data.contacts, `all_enquiries_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`);
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold glass text-gray-300 hover:text-white transition-all"
              title="Download ALL enquiries as Excel">
              <FaDownload />
              <span className="hidden sm:inline">All</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, service..."
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="glass rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  {["ID", "Name", "Email", "Phone", "Service", "Budget", "Status", "Date", "Actions"].map((h) => (
                    <th key={h} className="text-left text-gray-400 text-xs font-semibold px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-gray-500 py-8 text-sm">No Enquiries Found</td></tr>
                )}
                {filtered.map((c, i) => (
                  <tr key={c._id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-4 py-3 text-gray-600 text-xs">{i + 1}</td>
                    <td className="px-4 py-3 text-white text-sm font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.email}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.phone || "-"}</td>
                    <td className="px-4 py-3 text-gray-300 text-xs">{c.service}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.budget || "-"}</td>
                    <td className="px-4 py-3">
                      <select value={c.status} onChange={(e) => updateStatus(c._id, e.target.value)}
                        className="text-xs px-2 py-1 rounded-full font-semibold border-0 outline-none cursor-pointer"
                        style={{ background: `${statusColors[c.status]}20`, color: statusColors[c.status] }}>
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{new Date(c.createdAt).toLocaleDateString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setSelected(c)} className="w-7 h-7 glass rounded-lg flex items-center justify-center text-blue-400 hover:text-blue-300 text-xs">
                          <FaEye />
                        </button>
                        <button onClick={() => deleteContact(c._id)} className="w-7 h-7 glass rounded-lg flex items-center justify-center text-red-400 hover:text-red-300 text-xs">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filtered.length > 0 && (
            <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between">
              <span className="text-gray-500 text-xs">{filtered.length} Records Shown</span>
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => downloadExcel(filtered, `enquiries_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`)}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                <FaFileExcel /> Download Excel ({filtered.length} rows)
              </motion.button>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg glass rounded-2xl p-6 border border-white/10"
              style={{ background: "rgba(10,15,30,0.97)" }}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-bold text-lg">Enquiry Details</h2>
                <div className="flex items-center gap-2">
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    onClick={() => downloadExcel([selected], `enquiry_${selected.name.replace(/ /g, "_")}.xlsx`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                    style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                    <FaFileExcel /> Export
                  </motion.button>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white"><FaTimes /></button>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  ["Name", selected.name], ["Email", selected.email], ["Phone", selected.phone || "-"],
                  ["Company", selected.company || "-"], ["Service", selected.service], ["Budget", selected.budget || "-"],
                  ["Date", new Date(selected.createdAt).toLocaleString("en-IN")],
                ].map(([label, value]) => (
                  <div key={label} className="flex gap-3">
                    <span className="text-gray-500 text-sm w-20 flex-shrink-0">{label}:</span>
                    <span className="text-white text-sm">{value}</span>
                  </div>
                ))}
                <div>
                  <span className="text-gray-500 text-sm">Message:</span>
                  <p className="text-gray-300 text-sm mt-1 bg-white/5 rounded-xl p-3">{selected.message}</p>
                </div>
                <div className="flex gap-2 pt-2">
                  {statuses.map((s) => (
                    <button key={s} onClick={() => updateStatus(selected._id, s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                        ${selected.status === s ? "text-white" : "glass text-gray-400 hover:text-white"}`}
                      style={selected.status === s ? { background: statusColors[s] } : {}}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
